'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import useCustomRouter from '@/hooks/useCustomRouter';
import { Option } from '@/components/atoms/select/types';
import usePath from '@/hooks/usePath';
import { ContextShape, Location, LocationFull } from '@/components/organisms/shipping-and-language/types';
import { useSWRConfig } from 'swr';
import useAccount from '@/lib/hooks/useAccount';

const initialMarketState = {
  selectedLanguage: {} as Option,
  selectedLocation: {} as Location,
  locations: [] as Location[],
} as ContextShape;

export const ShipAndLanguageContext = createContext(initialMarketState);

type Props = {
  children: React.ReactNode;
  locations?: LocationFull[];
};
const ShipAndLanguageProvider = React.memo(({ children, locations = [] }: Props) => {
  const { mutate } = useSWRConfig();
  const router = useCustomRouter();
  const { path } = usePath();
  const { loggedIn, businessUnitKey } = useAccount();

  const { locale } = useParams();
  const memoizedLocations = useMemo(() => locations, [locations]);
  useEffect(() => {
    if (locale && loggedIn && businessUnitKey) {
      void mutate(['/action/cart/getCart', locale], undefined, { revalidate: true });
    }
  }, [locale, loggedIn, businessUnitKey, mutate]);

  const defaultLocationValue = (locale as string).split('-')[1];
  const [selectedLocationValue, setSelectedLocationValue] = useState<string>(defaultLocationValue);

  const selectedLocation = useMemo(
    () => locations.find((location) => location.value === selectedLocationValue),
    [locations, selectedLocationValue],
  );

  const selectedLanguage = useMemo(
    () => selectedLocation?.languages.find((language) => language.value === locale),
    [selectedLocation, locale],
  );

  const onLanguageSelect = useCallback(
    (language: string) => {
      router.push(path, { locale: language });
    },
    [router, path],
  );

  const onLocationSelect = useCallback(
    (location: string) => {
      const locationObject = locations.find((l) => l.value === location);

      setSelectedLocationValue(location);

      if (!locationObject?.languages.find((language) => language.value === locale)) {
        onLanguageSelect(locationObject?.defaultLanguage ?? '');
      }
    },
    [locations, locale, onLanguageSelect, setSelectedLocationValue],
  );

  const value = useMemo(
    () => ({
      selectedLanguage,
      selectedLocation,
      locations: memoizedLocations,
      onLanguageSelect,
      onLocationSelect,
    }),
    [selectedLanguage, selectedLocation, memoizedLocations, onLanguageSelect, onLocationSelect],
  );

  return <ShipAndLanguageContext.Provider value={value}>{children}</ShipAndLanguageContext.Provider>;
});
export default ShipAndLanguageProvider;
export const useShipAndLanguage = () => useContext(ShipAndLanguageContext);
